import { type JSX, type JSXElementConstructor } from 'react'
import { type SystemCssProperties } from '@dhl-ui/theme'
import { type DataTestIdAttribute } from '../../types'
import { BaseLink, type BaseLinkProps } from './BaseLink'

export type LinkProps<T extends keyof JSX.IntrinsicElements | JSXElementConstructor<never>> = Omit<
  BaseLinkProps<T>,
  'underline' | 'external' | 'variant'
> & {
  /**
   * color
   * @default primary
   */
  color?: 'primary' | 'dark' | 'light'

  /**
   * typography, only applicable when inline is false
   * default 'body1'
   */
  variant?: Extract<BaseLinkProps<T>['variant'], 'body1' | 'body2'>
} & (
    | {
        external?: never
        target?: never
        rel?: never
      }
    | {
        external: true
      }
  ) &
  (
    | { inline: true; variant?: never }
    | {
        inline?: false
        variant?: Extract<BaseLinkProps<T>['variant'], 'body1' | 'body2'>
      }
  ) & {
    sx?: Pick<SystemCssProperties, 'alignSelf'>
  } & DataTestIdAttribute

/**
 * Link
 *
 * Demos:
 * - [Link](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/primitives-link--docs)
 *
 */

export const Link = <T extends keyof JSX.IntrinsicElements | JSXElementConstructor<never>>(props: LinkProps<T>) => (
  <BaseLink {...props} />
)
